import React, { useState, useCallback, useEffect } from 'react';
import './BrokerDataImport.css';
import { getBrokerImport, saveBrokerImport } from './services/apiService';

/**
 * Broker Data Import Component
 * Uses backend API for broker data persistence
 */
const BrokerDataImport = ({ onClose }) => {
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // Load import history on mount
  useEffect(() => {
    loadImportHistory();
  }, []);

  const loadImportHistory = async () => {
    try {
      // Load history from a special "history" record
      const resp = await getBrokerImport('_history');
      if (resp?.data) {
        const data = typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data;
        if (data?.imports) {
          setHistory(data.imports);
        }
      }
    } catch {
      // No history yet, that's fine
    }
  };

  // Delete a single history item
  const deleteHistoryItem = async (index) => {
    try {
      const newHistory = history.filter((_, i) => i !== index);
      setHistory(newHistory);
      await saveBrokerImport('_history', 'system', {
        lastUpdated: new Date().toISOString(),
        imports: newHistory
      });
    } catch (error) {
      console.error('Failed to delete history item:', error);
    }
  };

  // Clear all history
  const clearAllHistory = async () => {
    if (window.confirm('Are you sure you want to clear all import history?')) {
      setHistory([]);
      try {
        await saveBrokerImport('_history', 'system', {
          lastUpdated: new Date().toISOString(),
          imports: []
        });
      } catch (error) {
        console.error('Failed to clear import history:', error);
      }
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processFile = async (file) => {
    setImporting(true);
    setResult(null);

    try {
      // 1. Extract date
      const dateMatch = file.name.match(/(\d{8})/);
      if (!dateMatch) {
        throw new Error('Cannot extract date from filename, please ensure filename contains date (e.g. 20260124)');
      }

      const dateStr = dateMatch[1];
      const date = `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
      const month = date.substring(0, 7);

      // 2. Read file
      const fileContent = await readFileContent(file);
      let data;

      if (file.name.endsWith('.json')) {
        data = JSON.parse(fileContent);
      } else if (file.name.endsWith('.csv')) {
        data = parseCSV(fileContent);
      } else {
        throw new Error('Unsupported file format, please upload JSON or CSV file');
      }

      // 3. Extract transactions
      const transactions = data.BrokerageTransactions || [];
      if (transactions.length === 0) {
        throw new Error('No transaction records found in file');
      }

      // 4. Group transactions by their actual month, then save each group
      const byMonth = {};
      transactions.forEach(t => {
        const tDate = t.Date ? new Date(t.Date) : null;
        const m = tDate && !isNaN(tDate) ? tDate.toISOString().substring(0, 7) : month;
        if (!byMonth[m]) byMonth[m] = [];
        byMonth[m].push(t);
      });

      let totalNew = 0;
      for (const [m, txns] of Object.entries(byMonth)) {
        totalNew += await saveToApi(date, m, file.name, txns);
      }
      const newRecords = totalNew;

      // 5. Record import history
      const importRecord = {
        timestamp: new Date().toISOString(),
        date: date,
        filename: file.name,
        recordCount: transactions.length,
        newRecords: newRecords
      };
      await saveImportHistory(importRecord);

      setResult({
        success: true,
        date: date,
        filename: file.name,
        totalRecords: transactions.length,
        newRecords: newRecords,
        storageMode: 'database',
        message: `Successfully imported ${transactions.length} records, ${newRecords} are new`
      });

      loadImportHistory();

    } catch (error) {
      console.error('Import failed:', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n');
    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());

    const transactions = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
      const transaction = {};
      headers.forEach((header, index) => {
        transaction[header] = values[index] || '';
      });

      if (transaction.Date) {
        transactions.push(transaction);
      }
    }

    return { BrokerageTransactions: transactions };
  };

  const saveToApi = async (date, month, filename, transactions) => {
    try {
      // Read existing month data from API
      let monthData;
      try {
        const resp = await getBrokerImport(month);
        monthData = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
      } catch {
        monthData = null;
      }

      if (!monthData) {
        monthData = {
          month: month,
          transactions: [],
          lastUpdate: new Date().toISOString()
        };
      }

      // Deduplicate and merge
      const existingIds = new Set(
        monthData.transactions.map(t =>
          `${t.Date}_${t.Symbol}_${t.Action}_${t.Quantity}_${t.Price}`
        )
      );

      let newCount = 0;
      transactions.forEach(t => {
        const id = `${t.Date}_${t.Symbol}_${t.Action}_${t.Quantity}_${t.Price}`;
        if (!existingIds.has(id)) {
          monthData.transactions.push({
            ...t,
            rawSource: filename,
            importDate: new Date().toISOString()
          });
          existingIds.add(id);
          newCount++;
        }
      });

      // Sort by date descending
      monthData.transactions.sort((a, b) => {
        const dateA = new Date(a.Date);
        const dateB = new Date(b.Date);
        return dateB - dateA;
      });

      monthData.lastUpdate = new Date().toISOString();

      // Save via API
      await saveBrokerImport(month, 'broker', monthData);

      console.log(`✅ Saved broker data to DB: ${month} (${newCount} new records)`);
      return newCount;

    } catch (error) {
      console.error('Failed to save broker data:', error);
      throw error;
    }
  };

  const saveImportHistory = async (importRecord) => {
    try {
      // Read existing history
      let imports = [...history];
      imports.unshift(importRecord);
      if (imports.length > 50) {
        imports = imports.slice(0, 50);
      }

      await saveBrokerImport('_history', 'system', {
        lastUpdated: new Date().toISOString(),
        imports: imports
      });
    } catch (error) {
      console.error('Failed to save import history:', error);
    }
  };

  const viewMonthData = async (month) => {
    try {
      const resp = await getBrokerImport(month);
      const data = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
      if (data) {
        alert(`${month} Data:\nTotal: ${data.transactions?.length || 0} records\nLast Update: ${data.lastUpdate ? new Date(data.lastUpdate).toLocaleString('en-US') : 'Unknown'}`);
      } else {
        alert(`No data for ${month}`);
      }
    } catch {
      alert(`No data for ${month}`);
    }
  };

  const exportMonthData = async (month) => {
    try {
      const resp = await getBrokerImport(month);
      const data = resp?.data ? (typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data) : null;
      if (data) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `broker-data-${month}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert(`No data for ${month}`);
      }
    } catch {
      alert(`No data for ${month}`);
    }
  };

  return (
    <div className="broker-import-overlay" onClick={onClose}>
      <div className="broker-import-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="broker-import-header">
          <h2>Import Broker Transaction Data</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="broker-import-content">
          {/* Drop Zone */}
          <div
            className={`drop-zone ${dragOver ? 'dragover' : ''} ${importing ? 'importing' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {importing ? (
              <div className="importing-state">
                <div className="spinner"></div>
                <p>Importing...</p>
              </div>
            ) : (
              <>
                <p className="drop-text">Drag broker transaction file here</p>
                <p className="drop-hint">Supports JSON and CSV formats</p>
                <label className="file-select-btn">
                  <input
                    type="file"
                    accept=".json,.csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  or click to select file
                </label>
              </>
            )}
          </div>

          {/* Import Result */}
          {result && (
            <div className={`import-result ${result.success ? 'success' : 'error'}`}>
              {result.success ? (
                <>
                  <h3>Import Successful!</h3>
                  <div className="result-details">
                    <p><strong>File:</strong> {result.filename}</p>
                    <p><strong>Date:</strong> {result.date}</p>
                    <p><strong>Total Records:</strong> {result.totalRecords}</p>
                    <p><strong>New Records:</strong> {result.newRecords}</p>
                  </div>
                </>
              ) : (
                <>
                  <h3>Import Failed</h3>
                  <p className="error-message">{result.error}</p>
                </>
              )}
            </div>
          )}

          {/* Import History */}
          <div className="import-history">
            <div className="history-header">
              <h3>Recent Imports</h3>
              {history.length > 0 && (
                <button
                  className="clear-history-btn"
                  onClick={clearAllHistory}
                  title="Clear all history"
                >
                  Clear All
                </button>
              )}
            </div>
            {history.length === 0 ? (
              <p className="no-history">No import records yet</p>
            ) : (
              <div className="history-list">
                {history.slice(0, 10).map((item, index) => (
                  <div key={index} className="history-item">
                    <div className="history-info">
                      <div className="history-date">
                        {item.date}
                      </div>
                      <div className="history-filename">{item.filename}</div>
                      <div className="history-stats">
                        {item.recordCount} records, {item.newRecords} new
                      </div>
                    </div>
                    <div className="history-actions">
                      <button
                        onClick={() => viewMonthData(item.date.substring(0, 7))}
                        className="view-btn"
                        title="View monthly data"
                      >
                        View
                      </button>
                      <button
                        onClick={() => exportMonthData(item.date.substring(0, 7))}
                        className="export-btn"
                        title="Export monthly data"
                      >
                        Export
                      </button>
                      <button
                        onClick={() => deleteHistoryItem(index)}
                        className="delete-btn"
                        title="Delete this record"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Usage Guide */}
          <div className="usage-guide">
            <h4>Usage Instructions</h4>
            <ol>
              <li>Download transaction history from broker app (select "Current Month" or "All")</li>
              <li>Drag the downloaded JSON or CSV file to the area above</li>
              <li>System will automatically identify date, deduplicate and archive data</li>
              <li>You can import daily, duplicate records will be skipped automatically</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerDataImport;
